import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes, { element } from "prop-types";
import AppContext from "../../AppContext";
import { langComponent, PAGE_STATE } from "../../constants";
import ShoppingContext from "../../contexts/ShoppingContext";
import useWorks from "../../hooks/useWorks";
import useUsers from "../../hooks/useUsers";
import SecondaryPagination from "../SecondaryPagination/SecondaryPagination";
import Filter from "./Filter";
import ProjectCard from "../ProjectCard";
import Chip from "../common/Chip";
import CircleArrowLeft from "../../assets/workExchange/arrow-left-circle.svg";
import CircleArrowRight from "../../assets/workExchange/arrow-right-circle.svg";

import { ReactComponent as PanoramaIndicatorSvg } from "../../assets/icons/360-indicator.svg";
import { ReactComponent as ImageIndicatorSvg } from "../../assets/icons/image-indicator.svg";

import { ReactComponent as FilterSvg } from "../../assets/icons/filter-primary.svg";
import { ReactComponent as SearchSvg } from "../../assets/header/search-primarycolor.svg";
import { ReactComponent as LeftArrowSvg } from "../../assets/header/left-arrow.svg";
import { ReactComponent as RemoveSvg } from "../../assets/filters/remove.svg";
import SearchBox from "../common/SearchBox";
import { useLocation } from "react-router-dom";
import _ from "lodash";
import { ReactComponent as EmptySvg } from "../../assets/icons/empty-projects.svg";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination, EffectFade } from "swiper/modules";
import ProjectDescription from "../common/ProjectDescription";
import ComboBox from "../common/ComboBox";

const assetsURI = import.meta.env.ASSETS_URI;

const CommunitySpace = ({ isParanoma }) => {
  const [_, setInit] = useState(false);
  const { viewProject, getLang, designStyles, plans } = useContext(AppContext);
  const TranslationHeader = getLang(langComponent.header);
  const { shoppingFilter, setShoppingFilter } = useContext(ShoppingContext);
  const { queryWorks } = useWorks();
  const { queryUsers, getUserRating, queryCompanies } = useUsers();
  const [projects, setProjects] = useState();
  const filterList = [
    { name: "Recommend", value: "recommend" },
    { name: "Latest", value: "new" },
    { name: "Oldest", value: "oldest", minWidth: 60 },
  ];
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [metadata, setMetadata] = useState(undefined);
  const [searchText, setSearchText] = useState(undefined);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [isToggleFilter, setIsToggleFilter] = useState(false);
  const [isToggleMobileSearch, setIsToggleMobileSearch] = useState(false);
  const location = useLocation();
  const [bannerWorks, setBannerWorks] = useState(undefined);

  useEffect(() => {
    setShoppingFilter({});
  }, []);

  useEffect(() => {
    init();
    return () => {};
  }, [shoppingFilter, searchText, selectedFilter]);

  const init = async (page, limit) => {
    const { typeOfWork, typeOfSpace, typeOfStyle, sort } = shoppingFilter || {};

    let _sort = sort;

    const filterArray = [
      {
        type: { $in: [1, 3] },
      },
      {
        active: true,
      },
      {
        deleted: false,
      },
    ];

    if (typeOfWork && typeOfWork.length > 0) {
      filterArray.push({ "designType.work": { $in: typeOfWork } });
    }

    if (typeOfSpace && typeOfSpace.length > 0) {
      filterArray.push({ "designType.space": { $in: typeOfSpace } });
    }

    if (typeOfStyle && typeOfStyle.length > 0) {
      filterArray.push({ "designType.style": { $in: typeOfStyle } });
    }

    if (searchText) {
      filterArray.push({
        $or: [
          { name: { $regex: searchText } },
          { desc: { $regex: searchText } },
        ],
      });
    }

    if (!selectedFilter) {
      _sort = {};
    }

    if (selectedFilter === "recommend") {
      _sort = {
        views: -1,
      };
    }

    if (selectedFilter === "latest") {
      _sort = {
        createdOn: -1,
      };
    }

    if (selectedFilter === "oldest") {
      _sort = {
        createdOn: 1,
      };
    }

    let filter = {};

    if (filterArray.length !== 0) {
      filter = {
        $and: filterArray,
      };
    }

    const { data: works, _metadata } = await queryWorks(
      filter,
      page || 1,
      limit || metadata?.pageSize || 12,
      {
        name: 1,
        desc: 1,
        banner: 1,
        createdBy: 1,
        createdOn: 1,
        views: 1,
        type: 1,
        program: 1,
        renderer: 1,
        company: 1,
        score: 1,
      },
      _sort || { createdOn: -1 }
    );

    if (works.length === 0) {
      setProjects(works);
      setMetadata(_metadata);
      setPageState(PAGE_STATE.IDLE);
      return;
    }

    // * Get users & companies
    const _userIds = works
      .filter((work) => !work.company)
      .map((d) => d.createdBy);

    const _companyIds = works
      .filter((work) => !!work.company)
      .map((d) => d.company);

    let _users = [];
    let _companies = [];

    if (_userIds.length > 0) {
      const { data } = await queryUsers({ _id: { $in: _userIds } });
      _users = data;
    }

    if (_companyIds.length > 0) {
      const { data } = await queryCompanies({
        _id: { $in: _companyIds },
      });

      _companies = data;
    }

    // * Map work with users and their ratings
    const results = [];

    for (const work of works) {
      if (work.company) {
        const company = _companies.find((u) => u._id === work.company);

        if (!company) {
          continue;
        }

        results.push({
          _id: work._id,
          banner: work.banner,
          panoramaExist: !!work.panorama,
          name: work.name,
          desc: work.desc,
          area: work.size,
          designType: work.designType,
          creator: {
            avatar: company.logo,
            name: company.name,
            rating: work.score ?? 0,
            plan: company.enterprise
              ? plans.find((p) => p.name === "Enterprise")?._id
              : plans.find((p) => p.name === "Business")?._id,
          },
          views: work.views,
          type: work.type,
          program: work.program,
          renderer: work.renderer,
        });
        continue;
      }

      const user = _users.find((u) => u._id === work.createdBy);

      if (!user) continue;

      results.push({
        _id: work._id,
        banner: work.banner,
        panoramaExist: !!work.panorama,
        name: work.name,
        desc: work.desc,
        area: work.size,
        designType: work.designType,
        creator: {
          avatar: user.avatar,
          name: `${user.firstname} ${user.surname || ""}`,
          rating: work.score ?? 0,
          plan: user.plan,
        },
        views: work.views,
        type: work.type,
        program: work.program,
        renderer: work.renderer,
      });
    }

    // * Get banner works
    getBannerWorks();

    setProjects(results);
    setMetadata(_metadata);
    setPageState(PAGE_STATE.IDLE);
  };

  const getBannerWorks = async () => {
    try {
      const { data: _workResults } = await queryWorks(
        {
          type: { $in: [1, 3] },
          active: true,
          deleted: false,
        },
        1,
        6,
        {
          name: 1,
          desc: 1,
          banner: 1,
          panorama: 1,
          createdBy: 1,
          createdOn: 1,
          company: 1,
          views: 1,
          type: 1,
          program: 1,
          renderer: 1,
          score: 1,
        },
        {
          createdOn: -1,
        }
      );

      const { data: _users } = await queryUsers(
        {
          _id: {
            $in: _workResults.map((w) => w.createdBy),
          },
        },
        1,
        _workResults.length
      );

      const { data: _companies } = await queryCompanies(
        {
          _id: {
            $in: _workResults.map((w) => w.company).filter((c) => !!c),
          },
        },
        1,
        _workResults.length,
        {
          logo: 1,
          name: 1,
        }
      );

      const _works = _workResults.map((w) => {
        const userFound = _users.find((u) => u._id === w.createdBy);
        if (userFound) w.user = userFound;

        if (w.company) {
          w.company = _companies.find((c) => c._id === w.company);
        }

        return w;
      });

      setBannerWorks(
        _works.map((work, index) => {
          work.banner = `${assetsURI}/${work.banner}`;
          work.rating = work.score ?? 0;
          return work;
        })
      );
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleWorkClick = (id) => {
    viewProject(id, location);
  };

  const handleOnPageChange = (page) => {
    setPageState(PAGE_STATE.LOADING);
    init(page);
  };

  const handleOnLimitChange = async (limit) => {
    init(undefined, limit);
  };

  const handleQuickFilterClick = (value) => {
    if (selectedFilter === value) {
      setSelectedFilter("");
      return;
    }

    setSelectedFilter(value);
  };

  // * Handle quick search
  const handleSearchChanged = (value) => {
    setSearchText(value);
  };

  const getActiveFilters = () => {
    const { typeOfWork, typeOfStyle, typeOfSpace } = shoppingFilter;

    const _active = [];
    let _remaining = 0;

    for (const type of typeOfWork ?? []) {
      if (_active.length >= 2) {
        _remaining++;
        continue;
      }

      _active.push(
        <div className="shopping-container-active-filters-item">
          <div className="shopping-container-active-filters-item__value">
            {designStyles.works.find((w) => w.value === type).name}
          </div>
          <RemoveSvg
            width={14}
            height={14}
            className="shopping-container-active-filters-item__remove"
            onClick={() => {
              setShoppingFilter((curr) => {
                return {
                  ...curr,
                  typeOfWork: curr.typeOfWork?.filter((_) => _ !== type) ?? [],
                };
              });
            }}
          />
        </div>
      );
    }

    for (const type of typeOfSpace ?? []) {
      if (_active.length >= 2) {
        _remaining++;
        continue;
      }

      _active.push(
        <div className="shopping-container-active-filters-item">
          <div className="shopping-container-active-filters-item__value">
            {designStyles.spaces.find((w) => w.value === type).name}
          </div>
          <RemoveSvg
            width={14}
            height={14}
            className="shopping-container-active-filters-item__remove"
            onClick={() => {
              setShoppingFilter((curr) => {
                return {
                  ...curr,
                  typeOfSpace:
                    curr.typeOfSpace?.filter((_) => _ !== type) ?? [],
                };
              });
            }}
          />
        </div>
      );
    }

    for (const type of typeOfStyle ?? []) {
      if (_active.length >= 2) {
        _remaining++;
        continue;
      }

      _active.push(
        <div className="shopping-container-active-filters-item">
          <div className="shopping-container-active-filters-item__value">
            {designStyles.styles.find((w) => w.value === type).name}
          </div>
          <RemoveSvg
            width={14}
            height={14}
            className="shopping-container-active-filters-item__remove"
            onClick={() => {
              setShoppingFilter((curr) => {
                return {
                  ...curr,
                  typeOfStyle:
                    curr.typeOfStyle?.filter((_) => _ !== type) ?? [],
                };
              });
            }}
          />
        </div>
      );
    }

    if (_remaining > 0) {
      _active.unshift(
        <div
          className="shopping-container-active-filters-item"
          style={{ padding: "2px 8px", cursor: "pointer" }}
          onClick={() => setIsToggleFilter(true)}
        >
          <div className="shopping-container-active-filters-item__value">
            + {_remaining}
          </div>
        </div>
      );
    }

    if (_active.length === 0) {
      return <></>;
    }

    return <div className="shopping-container-active-filters">{_active}</div>;
  };

  const getBannerDesc = (work) => {
    const {
      user: { _id, avatar, plan, firstname, surname },
      company,
      name,
      program,
      renderer,
      rating,
    } = work;

    // TODO: Rating score is hardcoded
    return (
      <ProjectDescription
        userId={_id}
        avatar={avatar}
        ownerName={`${firstname} ${surname ?? ""}`}
        company={company}
        workName={name}
        plan={plan}
        rating={rating ?? 0}
        program={program}
        renderer={renderer}
      />
    );
  };

  if (pageState !== PAGE_STATE.IDLE) {
    return <>Loading</>;
  }

  return (
    <div className="shopping">
      {bannerWorks?.length && (
        <div className="shopping-banner">
          <Swiper
            className="shopping-banner-carousel"
            modules={[Autoplay, Navigation, Pagination, EffectFade]}
            spaceBetween={0}
            slidesPerView={1}
            effect="fade"
            // autoplay={{
            //   delay: 5000,
            //   pauseOnMouseEnter: true,
            // }}
            // loop={true}
            navigation={{
              prevEl: ".shopping-banner-previous-button",
              nextEl: ".shopping-banner-next-button",
            }}
            speed={600}
            onInit={() => setInit(true)}
            pagination={{
              clickable: true,
            }}
          >
            {bannerWorks.map((work, index) => {
              return (
                <SwiperSlide
                  key={index}
                  className="shopping-banner-carousel-slide"
                >
                  <img
                    className="shopping-banner-carousel-slide__image"
                    alt=""
                    src={work.banner}
                    onClick={() => {
                      handleWorkClick(work._id);
                    }}
                  />
                  <div
                    className="shopping-banner-carousel-slide__indicator"
                    style={{
                      padding: work.type === 3 ? 14 : 10,
                    }}
                  >
                    {work.type === 1 && (
                      <PanoramaIndicatorSvg
                        className="shopping-banner__carousel-image-indicator__icon"
                        width={34}
                        onClick={() => handleWorkClick(work._id)}
                      />
                    )}
                    {work.type === 3 && (
                      <ImageIndicatorSvg
                        className="shopping-banner__carousel-image-indicator__icon"
                        width={24}
                        onClick={() => handleWorkClick(work._id)}
                      />
                    )}
                  </div>
                  <div className="shopping-banner-carousel-slide-footer">
                    {getBannerDesc(work)}
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
          <div className="shopping-header">
            <div className="shopping-header-sub">
              <div className="shopping-header__title">
                {TranslationHeader("Community Space")}
              </div>
              <div className="shopping-header__action">
                <img
                  className="shopping-banner-previous-button"
                  role="button"
                  src={CircleArrowLeft}
                  width={50}
                  height={50}
                  ref={prevRef}
                />
                <img
                  className="shopping-banner-next-button"
                  role="button"
                  src={CircleArrowRight}
                  width={50}
                  height={50}
                  ref={nextRef}
                />
              </div>
            </div>

            <div className="shopping-header__patch" />
            <div style={{ flexBasis: "100%" }} />
            <div className="shopping-header__patch" />
          </div>
        </div>
      )}
      <div className="shopping-container">
        {isToggleMobileSearch && (
          <div className="shopping-container-mobile-search">
            <LeftArrowSvg
              width={18}
              onClick={() => setIsToggleMobileSearch((curr) => !curr)}
            />
            <div style={{ flexGrow: 1 }}>
              <SearchBox
                name="search"
                // placeholder={TranslationCommon("Search design item")}
                onChange={handleSearchChanged}
              />
            </div>
          </div>
        )}
        {!isToggleMobileSearch && (
          <div className="shopping-container-filter">
            <div className="shopping-container-filter__quick">
              {filterList.map((data, index) => {
                return (
                  <Chip
                    id={`shopping-chip-category-${index}`}
                    label={data.name}
                    value={data.value}
                    type="single"
                    key={data.value}
                    previousValue={selectedFilter}
                    onClick={() => {
                      handleQuickFilterClick(data.value);
                    }}
                    selected={selectedFilter == data.value}
                    size={"small"}
                  />
                );
              })}
            </div>
            <div className="shopping-container-filter-dropdown">
              <ComboBox
                options={filterList}
                defaultValue={selectedFilter}
                width={160}
                onChange={({ value }) => {
                  handleQuickFilterClick(value);
                }}
              />
            </div>
            <div className="shopping-container-filter-right">
              <div className="shopping-container-filter__mobile-search">
                <SearchSvg
                  width={27}
                  onClick={() => {
                    setIsToggleMobileSearch((curr) => !curr);
                  }}
                />
              </div>
              <div className="shopping-container-filter__search">
                <SearchBox name="search" onChange={handleSearchChanged} />
              </div>
              <div
                className="shopping-container-filter-advanced"
                onClick={() => setIsToggleFilter((curr) => !curr)}
              >
                <FilterSvg width={28} />
                Filter
              </div>
            </div>
          </div>
        )}
        {shoppingFilter && getActiveFilters()}
        {projects && projects.length === 0 && pageState === PAGE_STATE.IDLE && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EmptySvg height={224} style={{ opacity: 0.3 }} />
            <span style={{ fontSize: 16, color: "#9d9d9d", marginTop: -8 }}>
              No results found
            </span>
          </div>
        )}
        <div className="shopping-container-projects">
          {projects?.map((project, index) => {
            return <ProjectCard key={`project-${index}`} {...project} />;
          })}
        </div>
        {/* <SecondaryPagination
          limits={[12, 15, 18, 21]}
          defaultLimit={metadata?.pageSize || 12}
          defaultPage={metadata?.currentPage || 1}
          pages={metadata?.totalPages || 1}
          onPageChange={handleOnPageChange}
          onLimitChange={handleOnLimitChange}
        /> */}
      </div>
      {isToggleFilter && (
        <Filter
          isMobile={false}
          onClose={() => {
            setIsToggleFilter((curr) => !curr);
          }}
        />
      )}
    </div>
  );
};

CommunitySpace.propTypes = { isParanoma: PropTypes.bool };

CommunitySpace.defaultProps = { isParanoma: false };

export default CommunitySpace;
